import React, { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { AddTransactionModalMutableConfig } from "../AddTransactionsModal.lib";
import {
    AddTransactionsModalFormErrors,
    AddTransactionsModalFormikConfig,
} from "../AddTransactionsModal";
import styles from "./Table.module.scss";

interface Props {
    loading: boolean;
    addLines: () => void;
}

export const Footer: React.FC<Props> = ({ loading, addLines }) => {
    const formikContext = useFormikContext<AddTransactionsModalFormikConfig>();
    const { values, handleSubmit } = formikContext;
    const hideAfterSubmit = values.isHideAfterSubmit;
    const errors = formikContext.errors as AddTransactionsModalFormErrors;

    const onSubmit = useCallback(
        () => async () => {
            AddTransactionModalMutableConfig.validate = true;
            handleSubmit();
        },
        [handleSubmit],
    );

    const shownError = useMemo(() => {
        if (errors.noTransactions) {
            return errors.noTransactions;
        }

        if (errors.financialAccountId) {
            return errors.financialAccountId;
        }

        if (errors.transactions && errors.transactions.length > 0) {
            const transactionErrorLines: string[] = [];

            (
                errors.transactions as Array<{
                    amount?: string;
                    date?: string;
                    category?: string;
                }>
            ).forEach((transaction) => {
                if (transaction.amount) {
                    transactionErrorLines.push(transaction.amount);
                }
                if (transaction.category) {
                    transactionErrorLines.push(transaction.category);
                }
                if (transaction.date) {
                    transactionErrorLines.push(transaction.date);
                }
            });

            if (transactionErrorLines.length > 1) {
                return "Please complete the highlighted fields";
            }

            if (transactionErrorLines.length === 1) {
                return transactionErrorLines[0];
            }
        }

        return undefined;
    }, [errors]);

    return (
        <div className={styles.modalFooter}>
            <div className={styles.footerButtons}>
                <div>
                    <Button variant="primary" size="md" onClick={addLines}>
                        Add lines
                    </Button>
                </div>

                <HStack align="center" gap={12}>
                    {shownError && (
                        <p className={styles.footerError}>{shownError}</p>
                    )}
                    <ButtonWithLoader
                        loading={loading && hideAfterSubmit}
                        disabled={!!shownError}
                        variant="primary"
                        size="md"
                        onClick={onSubmit()}
                        type="submit"
                    >
                        Save
                    </ButtonWithLoader>
                </HStack>
            </div>
        </div>
    );
};
